import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import html2canvas from "html2canvas";
import api from "@/services/api";
import {
  ArrowRight,
  Check,
  Maximize2,
  Info,
  FileText,
  AlignLeft,
  Grid3x3,
  Circle,
  Clock,
  Calendar,
  ListChecks,
  Upload,
  X,
  Instagram,
  MessageCircle,
  Palette,
  Image as ImageIcon,
  ArrowDown,
} from "lucide-react";
import {
  MAX_TOTAL_PAGES,
  FIXED_SECTIONS,
  FIXED_PAGES_TOTAL,
} from "@/data/agendaData";
import seccionesAgendaService, {
  SeccionAgenda,
} from "@/services/seccionesAgendaService";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PersonalizaAgendaResumen } from "@/components/agenda-builder/PersonalizaAgendaResumen";
import { useHybridCart } from "@/contexts/HybridCartContext";
import { PreviewCapas } from "@/components/personalizable/PreviewCapas";
import { PortadaConfig, Estampado } from "@/components/personalizable/types";

// Paleta de colores predeterminados
const COLORES_PREDETERMINADOS = [
  // Neutros esenciales
  { name: "Negro", rgb: "rgb(0, 0, 0)", hex: "#000000" },
  { name: "Blanco", rgb: "rgb(255, 255, 255)", hex: "#FFFFFF" },
  { name: "Gris claro", rgb: "rgb(230, 230, 230)", hex: "#E6E6E6" },
  { name: "Gris medio", rgb: "rgb(180, 180, 180)", hex: "#B4B4B4" },
  { name: "Gris oscuro", rgb: "rgb(90, 90, 90)", hex: "#5A5A5A" },

  // Tonos tierra / elegantes
  { name: "Beige", rgb: "rgb(232, 224, 210)", hex: "#E8E0D2" },
  { name: "Arena", rgb: "rgb(214, 206, 192)", hex: "#D6CEC0" },
  { name: "Taupe", rgb: "rgb(186, 176, 162)", hex: "#BAB0A2" },
  { name: "Marrón claro", rgb: "rgb(198, 176, 150)", hex: "#C6B096" },

  // Verdes neutros
  { name: "Verde oliva", rgb: "rgb(123, 131, 104)", hex: "#7B8368" },
  { name: "Verde salvia", rgb: "rgb(170, 183, 164)", hex: "#AAB7A4" },
  { name: "Verde bosque", rgb: "rgb(74, 94, 80)", hex: "#4A5E50" },

  // Azules suaves / profesionales
  { name: "Azul grisáceo", rgb: "rgb(156, 170, 183)", hex: "#9CAAB7" },
  { name: "Azul petróleo", rgb: "rgb(54, 79, 92)", hex: "#364F5C" },
  { name: "Azul marino", rgb: "rgb(35, 45, 65)", hex: "#232D41" },

  // Tonos suaves femeninos / neutros modernos
  { name: "Rosa nude", rgb: "rgb(224, 198, 193)", hex: "#E0C6C1" },
  { name: "Terracota suave", rgb: "rgb(193, 132, 110)", hex: "#C1846E" },
  { name: "Lavanda grisáceo", rgb: "rgb(192, 188, 201)", hex: "#C0BCC9" },
];

// Lista de estampados disponibles (fallback si la API falla)
const ESTAMPADOS_FALLBACK = [
  {
    id: "azul-hojas",
    name: "Azul Hojas",
    path: "https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/estampados-general/AZUL%20HOJAS.jpg",
    categoria: "general" as const,
  },
  {
    id: "beige-floral",
    name: "Beige Floral",
    path: "https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/estampados-general/BEIGE%20FLORAL.jpg",
    categoria: "general" as const,
  },
  {
    id: "beige-flores-gris",
    name: "Beige Flores Gris Oscuro",
    path: "https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/estampados-general/BEIGE%20FLORES%20GRIS%20OSCURO.jpg",
    categoria: "general" as const,
  },
  {
    id: "beige-hojas",
    name: "Beige Hojas",
    path: "https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/estampados-general/BEIGE%20HOJAS.jpg",
    categoria: "general" as const,
  },
  {
    id: "camel-suave",
    name: "Camel Suave",
    path: "https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/estampados-general/CAMEL%20SUAVE.jpg",
    categoria: "general" as const,
  },
  {
    id: "cerezas",
    name: "Cerezas",
    path: "https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/estampados-general/CEREZAS.jpg",
    categoria: "general" as const,
  },
  {
    id: "corazones-acanalados",
    name: "Corazones Acanalados",
    path: "https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/estampados-general/CORAZONES%20ACANALADOS.jpg",
    categoria: "general" as const,
  },
  {
    id: "corazones-acuarela",
    name: "Corazones Acuarela",
    path: "https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/estampados-general/CORAZONES%20ACUARELA.jpg",
    categoria: "general" as const,
  },
  {
    id: "florecitas-beige",
    name: "Florecitas Beige",
    path: "https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/estampados-general/FLORECITAS%20BEIGS.jpg",
    categoria: "general" as const,
  },
  {
    id: "flores-negras",
    name: "Flores Negras",
    path: "https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/estampados-general/FLORES%20NEGRAS.jpg",
    categoria: "general" as const,
  },
  {
    id: "mocca-floral",
    name: "Mocca Floral",
    path: "https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/estampados-general/MOCCA%20FLORAL.jpg",
    categoria: "general" as const,
  },
  {
    id: "mocca-hojas",
    name: "Mocca Hojas",
    path: "https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/estampados-general/MOCCA%20HOJAS.jpg",
    categoria: "general" as const,
  },
  {
    id: "negro-floral",
    name: "Negro Floral",
    path: "https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/estampados-general/NEGRO%20FLORAL.jpg",
    categoria: "general" as const,
  },
  {
    id: "puntos-mocca",
    name: "Puntos Mocca",
    path: "https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/estampados-general/PUNTOS%20MOCCA.jpg",
    categoria: "general" as const,
  },
  {
    id: "puntos-negros",
    name: "Puntos Negros",
    path: "https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/estampados-general/PUNTOS%20NEGROS.jpg",
    categoria: "general" as const,
  },
  {
    id: "verde-floral",
    name: "Verde Floral",
    path: "https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/estampados-general/VERDE%20FLORAL.jpg",
    categoria: "general" as const,
  },
  {
    id: "verde-suave",
    name: "Verde Suave",
    path: "https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/estampados-general/VERDE%20SUAVE.jpg",
    categoria: "general" as const,
  },
];

const PersonalizaAgenda = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { addToCart } = useHybridCart();
  const previewRef = useRef<HTMLDivElement>(null);
  const personalizacionSectionRef = useRef<HTMLElement>(null);
  const heroSectionRef = useRef<HTMLElement>(null);
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [showScrollHint, setShowScrollHint] = useState(false);

  // Función para hacer scroll suave a la sección de personalización
  const scrollToPersonalizacion = () => {
    personalizacionSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  // Detectar si el usuario está en la sección hero para mostrar el hint
  useEffect(() => {
    const handleScroll = () => {
      if (heroSectionRef.current && personalizacionSectionRef.current) {
        const heroRect = heroSectionRef.current.getBoundingClientRect();
        const personalizacionRect =
          personalizacionSectionRef.current.getBoundingClientRect();

        // Mostrar hint si el hero está visible y la sección de personalización no lo está
        const heroVisible = heroRect.bottom > 0;
        const personalizacionNotVisible =
          personalizacionRect.top > window.innerHeight;

        setShowScrollHint(heroVisible && personalizacionNotVisible);
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  // Secciones dinámicas desde la API
  const { data: seccionesData } = useQuery({
    queryKey: ["secciones-agenda"],
    queryFn: seccionesAgendaService.getSecciones,
    staleTime: 5 * 60 * 1000,
  });
  const sections: SeccionAgenda[] = seccionesData ?? [];

  // Fetch estampados SOLO de categoría "general" (para agendas 2026)
  const { data: estampadosData } = useQuery({
    queryKey: ["estampados", "general"],
    queryFn: async () => {
      const response = await api.get("/estampados?categoria=general");
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  // Usar estampados de la API (categoría general) o fallback al array local
  const ESTAMPADOS = estampadosData?.estampados || ESTAMPADOS_FALLBACK;
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{
    url: string;
    name: string;
  } | null>(null);
  // Estado genérico para variantes de secciones
  const [variantesSeleccionadas, setVariantesSeleccionadas] = useState<
    Record<string, string>
  >({});
  const [variantesModalSeccion, setVariantesModalSeccion] =
    useState<SeccionAgenda | null>(null);

  // Tablero borrable: estado separado (es obligatorio, manejo especial)
  const [tableroBorrableModalOpen, setTableroBorrableModalOpen] =
    useState(false);
  const [tableroBorrableType, setTableroBorrableType] = useState<string | null>(
    null
  );

  // QR Codes state
  const [qrInstagram, setQrInstagram] = useState<string | null>(null);
  const [qrWhatsapp, setQrWhatsapp] = useState<string | null>(null);
  const [isDraggingInstagram, setIsDraggingInstagram] = useState(false);
  const [isDraggingWhatsapp, setIsDraggingWhatsapp] = useState(false);

  // Personalización Visual state
  const [personalizationType, setPersonalizationType] = useState<
    "logo" | "estampado" | "estampado-personalizado" | null
  >(null);
  const [logo, setLogo] = useState<string | null>(null);
  const [logoPalette, setLogoPalette] = useState<string[]>([]);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [selectedEstampado, setSelectedEstampado] = useState<string | null>(
    null
  );
  const [customEstampado, setCustomEstampado] = useState<string | null>(null);
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const [isDraggingCustomEstampado, setIsDraggingCustomEstampado] =
    useState(false);

  // Nombre personalizado para portada y lomo
  const [personalizedName, setPersonalizedName] = useState<string>("");

  // Configuración de cubierta (argollas)
  const [tieneArgollas, setTieneArgollas] = useState<boolean>(true);

  // Convertir estado del CEO Planner al formato PortadaConfig para PreviewCapas
  const portadaConfig: PortadaConfig = useMemo(() => {
    // Caso 1: Estampado predefinido
    if (personalizationType === "estampado" && selectedEstampado) {
      return {
        tipo: "estampado",
        estampado: selectedEstampado,
      };
    }

    // Caso 2: Estampado/Imagen personalizada (upload del usuario)
    if (personalizationType === "estampado-personalizado" && customEstampado) {
      return {
        tipo: "imagen-personalizada",
        imagenPersonalizada: customEstampado,
      };
    }

    // Caso 3: Logo con color de fondo
    if (personalizationType === "logo" && logo) {
      return {
        tipo: "logo-color",
        logo: logo,
        color: selectedColor || "#ffffff",
      };
    }

    // Caso 4: Sin configuración
    return {
      tipo: "estampado",
    };
  }, [personalizationType, selectedEstampado, customEstampado, logo, selectedColor]);

  // Calcular páginas totales seleccionadas
  const selectedSectionsData = sections.filter((s) =>
    selectedSections.includes(s.id)
  );
  const customPagesUsed = selectedSectionsData.reduce(
    (sum, section) => sum + section.paginas,
    0
  );
  const totalPagesUsed = FIXED_PAGES_TOTAL + customPagesUsed;
  const pagesRemaining = MAX_TOTAL_PAGES - totalPagesUsed;

  const handleToggleSection = (sectionId: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return;

    const isSelected = selectedSections.includes(sectionId);

    // Si la sección tiene variantes y no está seleccionada → abrir modal de variantes
    if (!isSelected && section.variantes.length > 0) {
      setVariantesModalSeccion(section);
      return;
    }

    if (isSelected) {
      setSelectedSections((prev) => prev.filter((id) => id !== sectionId));
      setVariantesSeleccionadas((prev) => {
        const next = { ...prev };
        delete next[sectionId];
        return next;
      });
    } else {
      if (pagesRemaining >= section.paginas) {
        setSelectedSections((prev) => [...prev, sectionId]);
      }
    }
  };

  // Handler genérico de variante para cualquier sección
  const handleVarianteSelect = (slug: string) => {
    if (!variantesModalSeccion) return;
    const sectionId = variantesModalSeccion.id;
    const section = variantesModalSeccion;

    setVariantesSeleccionadas((prev) => ({ ...prev, [sectionId]: slug }));

    if (!selectedSections.includes(sectionId)) {
      const currentCustomPages = sections
        .filter((s) => selectedSections.includes(s.id))
        .reduce((sum, s) => sum + s.paginas, 0);
      const currentRemaining =
        MAX_TOTAL_PAGES - FIXED_PAGES_TOTAL - currentCustomPages;

      if (currentRemaining >= section.paginas) {
        setSelectedSections((prev) => [...prev, sectionId]);
      }
    }

    setVariantesModalSeccion(null);
  };

  const handleTableroBorrableTypeSelect = (type: string) => {
    setTableroBorrableType(type);
    // Tablero borrable es obligatorio, no suma páginas
  };

  const handleAddToCart = async () => {
    // Preparar objeto completo de la agenda personalizada
    const agendaConfig = {
      // Datos básicos
      precio: 35.0,
      totalPaginas: totalPagesUsed,
      paginasRestantes: pagesRemaining,

      // Secciones fijas (siempre incluidas)
      seccionesFijas: FIXED_SECTIONS,

      // Secciones personalizables seleccionadas
      seccionesPersonalizadas: selectedSectionsData.map((section) => ({
        id: section.id,
        nombre: section.nombre,
        paginas: section.paginas,
        ...(variantesSeleccionadas[section.id] && {
          variante: variantesSeleccionadas[section.id],
        }),
      })),

      // Tablero borrable (obligatorio)
      tableroBorrable: {
        tipo: tableroBorrableType,
      },

      // Códigos QR
      qrCodes: {
        instagram: qrInstagram,
        whatsapp: qrWhatsapp,
      },

      // Personalización visual
      personalizacionVisual: {
        tipo: personalizationType,
        ...(personalizationType === "logo" && {
          logo: logo,
          color: selectedColor,
        }),
        ...(personalizationType === "estampado" && {
          estampado: selectedEstampado,
          estampadoUrl: ESTAMPADOS.find((e) => e.id === selectedEstampado)
            ?.path, // URL original S3
        }),
        ...(personalizationType === "estampado-personalizado" && {
          customEstampado: customEstampado,
        }),
      },

      // Nombre personalizado (para portada y lomo)
      nombrePersonalizado: personalizedName || null,
    };

    // Capturar imagen del preview visual
    let previewImage = "/placeholder-agenda.jpg"; // Imagen por defecto

    // Determinar si necesitamos capturar con html2canvas
    // Si hay nombre personalizado O es logo/estampado personalizado, capturamos
    const needsCapture =
      personalizedName ||
      personalizationType === "logo" ||
      personalizationType === "estampado-personalizado";

    // Para estampados sin nombre personalizado, convertir a base64 para que se vea en el carrito
    if (
      personalizationType === "estampado" &&
      selectedEstampado &&
      !personalizedName
    ) {
      const estampadoUrl = ESTAMPADOS.find(
        (e) => e.id === selectedEstampado
      )?.path;

      if (estampadoUrl && (estampadoUrl.includes("amazonaws.com") || estampadoUrl.includes("r2.dev"))) {
        console.log(
          `📸 Convirtiendo estampado S3 a base64 para preview en carrito`
        );
        try {
          // Fetch la imagen desde S3 a través del proxy
          const proxyUrl = `${
            import.meta.env.VITE_API_URL || "http://localhost:5000"
          }/proxy/image?url=${encodeURIComponent(estampadoUrl)}`;
          const response = await fetch(proxyUrl);
          const blob = await response.blob();

          // Convertir a base64
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          // Comprimir la imagen
          const { compressBase64Image } = await import(
            "@/utils/imageValidator"
          );
          previewImage = await compressBase64Image(base64, 100);
          console.log(`✅ Estampado convertido a base64 y comprimido`);
        } catch (error) {
          console.error("❌ Error convirtiendo estampado a base64:", error);
          previewImage = estampadoUrl; // Fallback a URL directa
        }
      } else {
        previewImage = estampadoUrl || "/placeholder-agenda.jpg";
      }
    }
    // Capturar con html2canvas cuando hay personalización
    else if (needsCapture) {
      try {
        if (previewRef.current) {
          console.log(
            "📸 Capturando preview con html2canvas (incluye personalización)"
          );

          // Convertir imágenes S3 a base64 ANTES de capturar
          const images = previewRef.current.getElementsByTagName("img");
          console.log(`🖼️ Encontradas ${images.length} imágenes en el preview`);

          for (const img of Array.from(images)) {
            if (img.src.includes("amazonaws.com") || img.src.includes("r2.dev")) {
              console.log("🔄 Convirtiendo imagen S3 a base64 via proxy...");
              try {
                // Fetch la imagen desde S3 a través del proxy para evitar CORS
                const proxyUrl = `${
                  import.meta.env.VITE_API_URL || "http://localhost:5000"
                }/proxy/image?url=${encodeURIComponent(img.src)}`;
                const response = await fetch(proxyUrl);
                const blob = await response.blob();

                // Convertir a base64
                const base64 = await new Promise<string>((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result as string);
                  reader.onerror = reject;
                  reader.readAsDataURL(blob);
                });

                // Reemplazar src con base64
                img.src = base64;
                console.log("✅ Imagen convertida a base64");
              } catch (fetchError) {
                console.error(
                  "❌ Error convirtiendo imagen a base64:",
                  fetchError
                );
              }
            }
          }

          // Esperar un momento para que el DOM se actualice con las imágenes base64
          await new Promise((resolve) => setTimeout(resolve, 100));

          // Capturar el preview usando html2canvas
          console.log("🎨 Iniciando captura con html2canvas...");
          const canvas = await html2canvas(previewRef.current, {
            backgroundColor: "#FFFFFF",
            scale: 2,
            logging: false,
            allowTaint: true, // Permitir porque ya son base64
          });

          console.log(`📐 Canvas generado: ${canvas.width}x${canvas.height}px`);
          const rawPreview = canvas.toDataURL("image/png");
          console.log(
            `📦 Base64 sin comprimir: ${(rawPreview.length / 1024).toFixed(
              2
            )}KB`
          );

          // Comprimir la imagen del preview
          const { compressBase64Image } = await import(
            "@/utils/imageValidator"
          );
          previewImage = await compressBase64Image(rawPreview, 100); // 100KB target
          console.log(
            `📦 Base64 comprimido: ${(previewImage.length / 1024).toFixed(2)}KB`
          );

          console.log(`📸 Preview capturado y comprimido exitosamente`);
        }
      } catch (error) {
        console.warn("No se pudo capturar el preview con html2canvas:", error);

        // Fallback: Generar preview manualmente combinando estampado + texto
        if (
          personalizationType === "estampado" &&
          selectedEstampado &&
          personalizedName
        ) {
          console.log("🎨 Generando preview manualmente (estampado + texto)");
          try {
            const estampadoUrl = ESTAMPADOS.find(
              (e) => e.id === selectedEstampado
            )?.path;
            if (estampadoUrl) {
              // Crear canvas y cargar estampado
              const canvas = document.createElement("canvas");
              canvas.width = 800;
              canvas.height = 1000;
              const ctx = canvas.getContext("2d");

              if (ctx) {
                // Cargar imagen del estampado via proxy para evitar CORS
                console.log(
                  "🔄 Cargando estampado via proxy:",
                  estampadoUrl.substring(0, 80)
                );

                // Fetch la imagen a través del proxy
                const proxyUrl = `${
                  import.meta.env.VITE_API_URL || "http://localhost:5000"
                }/proxy/image?url=${encodeURIComponent(estampadoUrl)}`;
                const response = await fetch(proxyUrl);
                const blob = await response.blob();

                // Convertir a base64
                const base64 = await new Promise<string>((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result as string);
                  reader.onerror = reject;
                  reader.readAsDataURL(blob);
                });

                // Cargar la imagen base64 en el canvas
                const img = new Image();

                await new Promise((resolve, reject) => {
                  img.onload = () => {
                    console.log("✅ Estampado cargado para preview manual");
                    // Dibujar estampado
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                    // Agregar texto personalizado en la esquina inferior derecha
                    ctx.font = "32px serif";
                    ctx.fillStyle = "#4A3933"; // brown-dark
                    ctx.textAlign = "right";
                    ctx.textBaseline = "bottom";

                    // Sombra para mejor legibilidad
                    ctx.shadowColor = "rgba(255, 255, 255, 0.8)";
                    ctx.shadowBlur = 4;
                    ctx.shadowOffsetX = 2;
                    ctx.shadowOffsetY = 2;

                    ctx.fillText(
                      personalizedName,
                      canvas.width - 40,
                      canvas.height - 40
                    );
                    console.log(
                      `✅ Texto "${personalizedName}" agregado al preview`
                    );

                    resolve(canvas);
                  };
                  img.onerror = (err) => {
                    console.error("❌ Error cargando estampado:", err);
                    reject(err);
                  };

                  img.src = base64;
                });

                // Convertir a base64 y comprimir
                const rawPreview = canvas.toDataURL("image/png");
                const { compressBase64Image } = await import(
                  "@/utils/imageValidator"
                );
                previewImage = await compressBase64Image(rawPreview, 100);

                console.log("✅ Preview manual generado exitosamente");
              }
            }
          } catch (manualError) {
            console.error("Error generando preview manual:", manualError);
            // Último fallback: convertir estampado a base64
            const estampadoUrl = ESTAMPADOS.find(
              (e) => e.id === selectedEstampado
            )?.path;

            if (estampadoUrl && (estampadoUrl.includes("amazonaws.com") || estampadoUrl.includes("r2.dev"))) {
              try {
                console.log("🔄 Fallback: convirtiendo estampado a base64...");
                const proxyUrl = `${
                  import.meta.env.VITE_API_URL || "http://localhost:5000"
                }/proxy/image?url=${encodeURIComponent(estampadoUrl)}`;
                const response = await fetch(proxyUrl);
                const blob = await response.blob();

                const base64 = await new Promise<string>((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result as string);
                  reader.onerror = reject;
                  reader.readAsDataURL(blob);
                });

                const { compressBase64Image } = await import(
                  "@/utils/imageValidator"
                );
                previewImage = await compressBase64Image(base64, 100);
              } catch (fallbackError) {
                console.error("❌ Error en último fallback:", fallbackError);
                previewImage = estampadoUrl || "/placeholder-agenda.jpg";
              }
            } else {
              previewImage = estampadoUrl || "/placeholder-agenda.jpg";
            }
          }
        } else if (personalizationType === "estampado" && selectedEstampado) {
          // Este caso ya no debería llegar aquí, pero por si acaso
          const estampadoUrl = ESTAMPADOS.find(
            (e) => e.id === selectedEstampado
          )?.path;

          if (estampadoUrl && (estampadoUrl.includes("amazonaws.com") || estampadoUrl.includes("r2.dev"))) {
            try {
              console.log(
                "🔄 Convirtiendo estampado a base64 (else branch)..."
              );
              const proxyUrl = `${
                import.meta.env.VITE_API_URL || "http://localhost:5000"
              }/proxy/image?url=${encodeURIComponent(estampadoUrl)}`;
              const response = await fetch(proxyUrl);
              const blob = await response.blob();

              const base64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });

              const { compressBase64Image } = await import(
                "@/utils/imageValidator"
              );
              previewImage = await compressBase64Image(base64, 100);
            } catch (error) {
              console.error("❌ Error convirtiendo estampado:", error);
              previewImage = estampadoUrl || "/placeholder-agenda.jpg";
            }
          } else {
            previewImage = estampadoUrl || "/placeholder-agenda.jpg";
          }
        } else if (personalizationType === "logo" && logo) {
          previewImage = logo;
        } else if (
          personalizationType === "estampado-personalizado" &&
          customEstampado
        ) {
          previewImage = customEstampado;
        }
      }
    }

    // Crear un producto compatible con el carrito
    const agendaProduct = {
      _id: `agenda-personalizada-${Date.now()}`, // ID único temporal
      name: "Agenda Personalizada 2026",
      price: 35.0,
      image: previewImage, // Usar la imagen capturada del preview
      stock: 999, // Stock ilimitado para agendas personalizadas
      category: "agendas-personalizadas",
      active: true,
    };

    console.log("📋 Agenda product created:", {
      id: agendaProduct._id,
      hasImage: !!agendaProduct.image,
      imagePreview: agendaProduct.image.substring(0, 50) + "...",
      imageSize: new Blob([agendaProduct.image]).size,
    });

    // Crear notas de personalización para mostrar en el carrito
    const buildCustomizationNotes = () => {
      const notes: string[] = [];

      // Tablero borrable
      notes.push(
        `Tablero: ${
          tableroBorrableType === "semana" ? "Semana" : "Ideas/Bocetos"
        }`
      );

      // Secciones
      if (selectedSectionsData.length > 0) {
        notes.push(`${selectedSectionsData.length} secciones personalizadas`);
      }

      // Personalización visual
      if (personalizationType === "logo") {
        notes.push(`Portada con logo personalizado`);
      } else if (personalizationType === "estampado") {
        const estampadoName = ESTAMPADOS.find(
          (e) => e.id === selectedEstampado
        )?.name;
        notes.push(`Estampado: ${estampadoName}`);
      }

      // QR codes
      if (qrInstagram) notes.push("QR Instagram");
      if (qrWhatsapp) notes.push("QR WhatsApp");

      return notes.join(" | ");
    };

    try {
      // Agregar al carrito con toda la configuración
      await addToCart(
        agendaProduct,
        1, // cantidad
        {
          personalizedName: "Agenda Personalizada", // Nombre visible en el carrito
          notes: buildCustomizationNotes(), // Resumen de personalizaciones
        }
      );

      // Guardar configuración completa en localStorage para usarla en checkout
      localStorage.setItem(
        `agenda-config-${agendaProduct._id}`,
        JSON.stringify(agendaConfig)
      );

      console.log("Agenda configurada y agregada al carrito:", {
        product: agendaProduct,
        config: agendaConfig,
      });

      // Toast ya se muestra por el HybridCartContext
      // Navegar al carrito
      navigate("/cart");
    } catch (error) {
      console.error("Error al agregar agenda al carrito:", error);
      toast({
        title: "Error",
        description:
          "No se pudo agregar la agenda al carrito. Intenta de nuevo.",
        variant: "destructive",
      });
    }
  };

  const handleOpenImageModal = (
    imageUrl: string,
    sectionName: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    setSelectedImage({ url: imageUrl, name: sectionName });
    setImageModalOpen(true);
  };

  // Funciones de manejo de QR codes
  const handleQrDragOver = (
    e: React.DragEvent,
    type: "instagram" | "whatsapp"
  ) => {
    e.preventDefault();
    if (type === "instagram") {
      setIsDraggingInstagram(true);
    } else {
      setIsDraggingWhatsapp(true);
    }
  };

  const handleQrDragLeave = (type: "instagram" | "whatsapp") => {
    if (type === "instagram") {
      setIsDraggingInstagram(false);
    } else {
      setIsDraggingWhatsapp(false);
    }
  };

  const handleQrDrop = async (
    e: React.DragEvent,
    type: "instagram" | "whatsapp"
  ) => {
    e.preventDefault();
    if (type === "instagram") {
      setIsDraggingInstagram(false);
    } else {
      setIsDraggingWhatsapp(false);
    }

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      const file = files[0];
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const result = event.target?.result as string;
          if (type === "instagram") {
            setQrInstagram(result);
          } else {
            setQrWhatsapp(result);
          }
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleQrFileSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "instagram" | "whatsapp"
  ) => {
    const files = e.target.files;
    if (files && files[0]) {
      const file = files[0];
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const result = event.target?.result as string;
          if (type === "instagram") {
            setQrInstagram(result);
          } else {
            setQrWhatsapp(result);
          }
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleQrRemove = (type: "instagram" | "whatsapp") => {
    if (type === "instagram") {
      setQrInstagram(null);
    } else {
      setQrWhatsapp(null);
    }
  };

  // Funciones de personalización visual
  const extractColorsFromImage = (imageDataUrl: string): Promise<string[]> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve([]);
          return;
        }

        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;

        // Muestreo de colores más agresivo
        const colorMap: Record<string, number> = {};
        for (let i = 0; i < pixels.length; i += 16) {
          const r = pixels[i];
          const g = pixels[i + 1];
          const b = pixels[i + 2];
          const a = pixels[i + 3];

          // Solo ignorar píxeles completamente transparentes o blancos puros
          if (a < 50) continue;
          if (r > 250 && g > 250 && b > 250) continue;

          // Cuantizar colores similares (agrupar en rangos de 15)
          const qr = Math.round(r / 15) * 15;
          const qg = Math.round(g / 15) * 15;
          const qb = Math.round(b / 15) * 15;

          const hex = `#${((1 << 24) + (qr << 16) + (qg << 8) + qb)
            .toString(16)
            .slice(1)}`;
          colorMap[hex] = (colorMap[hex] || 0) + 1;
        }

        // Obtener los 8 colores más frecuentes (para tener más variedad)
        const sortedColors = Object.entries(colorMap)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 8)
          .map(([color]) => color);

        // Si tenemos menos de 4 colores, agregar algunas alternativas neutras
        if (sortedColors.length < 4) {
          const neutrals = ["#C9B8A0", "#E8DFD8", "#A89584", "#D4C4B6"];
          sortedColors.push(
            ...neutrals.filter((c) => !sortedColors.includes(c))
          );
        }

        resolve(sortedColors.slice(0, 8));
      };
      img.src = imageDataUrl;
    });
  };

  const handlePersonalizationTypeChange = (
    type: "logo" | "estampado" | "estampado-personalizado"
  ) => {
    setPersonalizationType(type);
    // Limpiar los otros tipos
    if (type === "logo") {
      setSelectedEstampado(null);
      setCustomEstampado(null);
    } else if (type === "estampado") {
      setLogo(null);
      setLogoPalette([]);
      setSelectedColor(null);
      setCustomEstampado(null);
    } else if (type === "estampado-personalizado") {
      setLogo(null);
      setLogoPalette([]);
      setSelectedColor(null);
      setSelectedEstampado(null);
    }
  };

  const handleLogoDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingLogo(true);
  };

  const handleLogoDragLeave = () => {
    setIsDraggingLogo(false);
  };

  const handleLogoDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingLogo(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      const file = files[0];
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = async (event) => {
          const result = event.target?.result as string;
          setLogo(result);
          // Extraer paleta de colores
          const colors = await extractColorsFromImage(result);
          setLogoPalette(colors);
          if (colors.length > 0) {
            setSelectedColor(colors[0]); // Auto-seleccionar el color más prominente
          }
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleLogoFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.target.files;
    if (files && files[0]) {
      const file = files[0];
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = async (event) => {
          const result = event.target?.result as string;
          setLogo(result);
          // Extraer paleta de colores
          const colors = await extractColorsFromImage(result);
          setLogoPalette(colors);
          if (colors.length > 0) {
            setSelectedColor(colors[0]);
          }
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleLogoRemove = () => {
    setLogo(null);
    setLogoPalette([]);
    setSelectedColor(null);
  };

  const handleEstampadoSelect = (estampado: string) => {
    setSelectedEstampado(estampado);
  };

  // Funciones para estampado personalizado
  const handleCustomEstampadoDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingCustomEstampado(true);
  };

  const handleCustomEstampadoDragLeave = () => {
    setIsDraggingCustomEstampado(false);
  };

  const handleCustomEstampadoDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingCustomEstampado(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      const file = files[0];
      // Validar tamaño (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "Archivo muy grande",
          description: "El archivo no debe superar los 5MB",
          variant: "destructive",
        });
        return;
      }
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const result = event.target?.result as string;
          setCustomEstampado(result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleCustomEstampadoFileSelect = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.target.files;
    if (files && files[0]) {
      const file = files[0];
      // Validar tamaño (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "Archivo muy grande",
          description: "El archivo no debe superar los 5MB",
          variant: "destructive",
        });
        return;
      }
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const result = event.target?.result as string;
          setCustomEstampado(result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleCustomEstampadoRemove = () => {
    setCustomEstampado(null);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section
        ref={heroSectionRef}
        className="bg-gradient-to-b from-peach/20 to-background py-16 md:py-24"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="font-playfair text-3xl md:text-4xl font-bold text-brown-dark leading-tight">
              Eres la CEO de tu vida
            </h1>
            <div className="space-y-4 text-lg md:text-xl text-brown-light max-w-3xl mx-auto">
              <p className="text-base md:text-lg">
                Y como la CEO, tienes el poder de diseñar las herramientas que
                te acompañan.
                <br />
                <br />
                En los siguientes pasos podrás personalizar tu agenda según tu
                estilo, tus necesidades y tu momento de vida. Elige con
                intención. Organiza con sentido. Tu agenda será el reflejo de la
                vida que estás construyendo.
                <br />
                <br />
                Tu agenda personalizada tendrá un total de{" "}
                <span className="font-medium text-brown">
                  {MAX_TOTAL_PAGES} páginas.
                </span>{" "}
                Cada sección indica cuántas páginas utiliza, para que puedas
                personalizar tu agenda sin superar el total disponible. <br />
                <br />
                <span className="font-medium text-brown">
                  Todas las agendas incluyen ({FIXED_PAGES_TOTAL} páginas):
                  <br />
                  {FIXED_SECTIONS.map((section, index) => (
                    <span key={index}>
                      {section.name} ({section.pages}{" "}
                      {section.pages === 1 ? "página" : "páginas"})
                      {index < FIXED_SECTIONS.length - 1 ? ", " : "."}
                    </span>
                  ))}
                </span>
                <br />
                <br />
                Estas {FIXED_PAGES_TOTAL} páginas fijas ya están incluidas
                dentro de las{" "}
                <span className="font-medium text-brown">
                  {MAX_TOTAL_PAGES} páginas totales.
                </span>{" "}
                Te quedan{" "}
                <span className="font-medium text-brown">
                  {MAX_TOTAL_PAGES - FIXED_PAGES_TOTAL} páginas disponibles
                </span>{" "}
                para personalizar con las secciones que elijas.
                <br />
                <br />
                <span className="font-medium text-brown">
                  Si quedan páginas disponibles, se añadirán automáticamente a
                  hojas de organización diaria o, en agendas solo semanales, a
                  hojas de notas libres.
                </span>
              </p>
              {/* <p>
                Más que papel, una herramienta{" "}
                <span className="italic font-medium text-brown">
                  ideal para acompañar a quienes escriben su historia.
                </span>
              </p>
              <p className="text-base md:text-lg">
                Su concepto introspectivo, reconocido ante el INDAUTOR, nos
                recuerda{" "}
                <span className="font-medium text-brown">
                  la importancia de seguir brillando con luz propia, y escribir
                  con intención el siguiente capítulo de nuestra historia.
                </span>
              </p>
              <p className="text-base md:text-lg">
                Cada agenda incluye planificadores anuales, calendarios
                mensuales, trackers de hábitos y emociones, planner de finanzas,
                frases, retos mensuales y secciones introspectivas originales.
              </p>
              <p className="text-xl md:text-2xl font-playfair italic text-brown-dark mt-8">
                Nuestra misión es recordarte que el balance entre hacer y sentir
                existe, escribiendo tu historia una página a la vez.
              </p> */}
            </div>
          </div>
        </div>
      </section>

      {/* Secciones Disponibles */}
      <section
        ref={personalizacionSectionRef}
        className="py-6 md:py-8 bg-gradient-to-b from-background to-peach/30"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-primary-dark font-medium tracking-wide uppercase text-sm">
                Personaliza tu Agenda
              </span>
              <h2 className="font-playfair text-3xl md:text-5xl font-semibold text-brown mt-2 mb-4">
                Crea tu agenda paso a paso,{" "}
                <span className="italic">elige tus secciones:</span>
              </h2>
              <div className="w-24 h-1 bg-primary mx-auto mt-4 mb-6 rounded-full"></div>
              <p className="text-brown-light/90 text-lg max-w-2xl mx-auto mb-3">
                Elige las secciones que mejor se adapten a tu estilo de vida
              </p>
              <p className="text-brown-light/70 text-base max-w-xl mx-auto">
                Tu agenda tiene un máximo de{" "}
                <span className="font-semibold text-brown-dark">
                  {MAX_TOTAL_PAGES} páginas
                </span>
                . Selecciona las secciones que necesites sin exceder este
                límite.
              </p>
            </div>

            {/* Layout de 2 columnas: Contenido + Resumen */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* COLUMNA IZQUIERDA (2/3) - Contenido */}
              <div className="lg:col-span-2 space-y-12">
                {/* Sección de Tablero Borrable - Obligatorio */}
                <div className="mt-12 max-w-4xl mx-auto mb-16">
                  <div className="text-center mb-10">
                    <h3 className="font-playfair text-3xl md:text-4xl font-semibold text-brown mb-4">
                      Tablero Borrable
                    </h3>
                    <p className="text-brown-light/90 text-lg max-w-2xl mx-auto">
                      Selecciona el tipo de tablero borrable que prefieres para
                      la portada interna de tu agenda.
                    </p>
                  </div>

                  <Card className="bg-gradient-to-br from-peach/20 to-primary/10 border-2 border-primary/30">
                    <CardContent className="p-8">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Tablero Semana */}
                        <div
                          onClick={() =>
                            handleTableroBorrableTypeSelect("semana")
                          }
                          className={`relative border-2 rounded-lg p-6 transition-all duration-300 cursor-pointer ${
                            tableroBorrableType === "semana"
                              ? "border-primary bg-primary/10 ring-2 ring-primary shadow-lg"
                              : "border-brown-light/30 bg-white hover:border-primary/50 hover:bg-primary/5"
                          }`}
                        >
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <h4 className="font-playfair text-xl font-semibold text-brown-dark">
                                Semana
                              </h4>
                              {tableroBorrableType === "semana" && (
                                <div className="bg-primary rounded-full p-1.5">
                                  <Check className="h-5 w-5 text-brown-dark" />
                                </div>
                              )}
                            </div>

                            <div className="w-full h-48 bg-white border-2 border-brown-light/20 rounded-lg flex items-center justify-center overflow-hidden">
                              <img
                                src="https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/general/TABLERO%20BORRABLE%20SEMANA.png"
                                alt="Tablero Borrable Semana"
                                className="w-full h-full object-contain p-2"
                              />
                            </div>

                            <p className="text-sm text-brown-light/80 text-center">
                              Organiza tus tareas semanales con este tablero
                              borrable
                            </p>
                          </div>
                        </div>

                        {/* Tablero Ideas */}
                        <div
                          onClick={() =>
                            handleTableroBorrableTypeSelect("ideas")
                          }
                          className={`relative border-2 rounded-lg p-6 transition-all duration-300 cursor-pointer ${
                            tableroBorrableType === "ideas"
                              ? "border-primary bg-primary/10 ring-2 ring-primary shadow-lg"
                              : "border-brown-light/30 bg-white hover:border-primary/50 hover:bg-primary/5"
                          }`}
                        >
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <h4 className="font-playfair text-xl font-semibold text-brown-dark">
                                Ideas / Bocetos
                              </h4>
                              {tableroBorrableType === "ideas" && (
                                <div className="bg-primary rounded-full p-1.5">
                                  <Check className="h-5 w-5 text-brown-dark" />
                                </div>
                              )}
                            </div>

                            <div className="w-full h-48 bg-white border-2 border-brown-light/20 rounded-lg flex items-center justify-center overflow-hidden">
                              <img
                                src="https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/general/TABLERO%20BORRABLE%20IDEAS.png"
                                alt="Tablero Borrable Ideas"
                                className="w-full h-full object-contain p-2"
                              />
                            </div>

                            <p className="text-sm text-brown-light/80 text-center">
                              Espacio libre para capturar tus ideas y bocetos
                            </p>
                          </div>
                        </div>
                      </div>

                      <Alert className="mt-6 bg-blue-50 border-blue-200">
                        <Info className="h-4 w-4 text-blue-600" />
                        <AlertDescription className="text-brown-light/80">
                          El tablero borrable no suma páginas a tu agenda. Es
                          una funcionalidad incluida en la portada interna.
                        </AlertDescription>
                      </Alert>
                    </CardContent>
                  </Card>
                </div>

                {/* Grid de Secciones */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {sections.filter((s) => s.paginas > 0).map((section) => {
                    const isSelected = selectedSections.includes(section.id);
                    const isDisabled =
                      !isSelected && pagesRemaining < section.paginas;
                    const tieneVariantes = section.variantes.length > 0;
                    const varianteSeleccionada =
                      variantesSeleccionadas[section.id];

                    return (
                      <Card
                        key={section.id}
                        onClick={() => handleToggleSection(section.id)}
                        className={`cursor-pointer transition-all duration-300 hover:shadow-2xl group relative overflow-hidden bg-[#F8F6F4] rounded-2xl ${
                          isSelected
                            ? "ring-2 ring-primary shadow-lg scale-[1.02]"
                            : isDisabled
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:scale-[1.02]"
                        }`}
                      >
                        {/* Checkmark Badge */}
                        {isSelected && (
                          <div className="absolute top-4 right-4 z-10 bg-primary rounded-full p-2 shadow-lg">
                            <Check className="h-5 w-5 text-brown-dark" />
                          </div>
                        )}

                        <CardContent className="p-6 space-y-4">
                          {/* Imagen */}
                          <div className="relative flex items-center justify-center h-32 overflow-hidden rounded-lg bg-white group/image">
                            {section.imageUrl ? (
                              <>
                                <img
                                  src={section.imageUrl}
                                  alt={section.nombre}
                                  className="w-full h-full object-contain p-2 transition-transform duration-300 group-hover/image:scale-110"
                                />
                                {/* Botón para maximizar imagen */}
                                <button
                                  onClick={(e) =>
                                    handleOpenImageModal(
                                      section.imageUrl!,
                                      section.nombre,
                                      e
                                    )
                                  }
                                  className="absolute top-2 right-2 bg-brown-dark/80 hover:bg-brown-dark text-white p-1.5 rounded-md opacity-0 group-hover/image:opacity-100 transition-opacity duration-200 z-10"
                                  aria-label="Ver imagen en grande"
                                >
                                  <Maximize2 className="h-4 w-4" />
                                </button>
                              </>
                            ) : (
                              <span className="text-6xl">{section.icon}</span>
                            )}
                          </div>

                          {/* Nombre */}
                          <h4 className="font-playfair font-semibold text-lg text-center text-brown-dark group-hover:text-primary-dark transition-colors">
                            {section.nombre}
                          </h4>

                          {/* Badge de variante seleccionada (genérico) */}
                          {tieneVariantes && isSelected && varianteSeleccionada && (
                            <div className="flex justify-center">
                              <Badge className="bg-primary/80 text-brown-dark">
                                {section.variantes.find(
                                  (v) => v.slug === varianteSeleccionada
                                )?.nombre ?? varianteSeleccionada}
                              </Badge>
                            </div>
                          )}

                          {/* Descripción */}
                          <p className="text-sm text-brown-light/90 text-center min-h-[40px] leading-relaxed">
                            {section.descripcion}
                          </p>

                          {/* Metadata */}
                          <div className="flex items-center justify-center pt-4 border-t border-primary/20">
                            <span className="text-xs text-brown-light/80">
                              {section.paginas} páginas
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Sección de QR Codes */}
                <div className="mt-20 max-w-4xl mx-auto">
                  <div className="text-center mb-10">
                    <h3 className="font-playfair text-3xl md:text-4xl font-semibold text-brown mb-4">
                      Códigos QR para tus Redes Sociales
                    </h3>
                    <p className="text-brown-light/90 text-lg max-w-2xl mx-auto">
                      Adjunta tus códigos QR de Instagram y WhatsApp. Se
                      imprimirán en la portada interna trasera de tu agenda.
                    </p>
                  </div>

                  <Card className="bg-gradient-to-br from-peach/20 to-primary/10 border-2 border-primary/30">
                    <CardContent className="p-8">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Instagram QR */}
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 justify-center mb-4">
                            <Instagram className="h-6 w-6 text-pink-600" />
                            <h4 className="font-playfair text-xl font-semibold text-brown-dark">
                              Instagram
                            </h4>
                          </div>

                          <div
                            onDragOver={(e) => handleQrDragOver(e, "instagram")}
                            onDragLeave={() => handleQrDragLeave("instagram")}
                            onDrop={(e) => handleQrDrop(e, "instagram")}
                            className={`relative border-2 border-dashed rounded-lg p-6 transition-all duration-300 ${
                              isDraggingInstagram
                                ? "border-primary bg-primary/10 scale-105"
                                : "border-brown-light/30 bg-white hover:border-primary/50 hover:bg-primary/5"
                            }`}
                          >
                            {qrInstagram ? (
                              <div className="relative group">
                                <img
                                  src={qrInstagram}
                                  alt="QR Instagram"
                                  className="w-full h-48 object-contain rounded-lg"
                                />
                                <button
                                  onClick={() => handleQrRemove("instagram")}
                                  className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                  aria-label="Eliminar QR de Instagram"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <label className="flex flex-col items-center justify-center cursor-pointer min-h-[200px]">
                                <Upload className="h-12 w-12 text-brown-light/50 mb-3" />
                                <p className="text-brown-dark font-medium mb-1">
                                  Arrastra tu QR aquí
                                </p>
                                <p className="text-sm text-brown-light/70 mb-3">
                                  o haz clic para seleccionar
                                </p>
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) =>
                                    handleQrFileSelect(e, "instagram")
                                  }
                                  className="hidden"
                                />
                              </label>
                            )}
                          </div>
                        </div>

                        {/* WhatsApp QR */}
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 justify-center mb-4">
                            <MessageCircle className="h-6 w-6 text-green-600" />
                            <h4 className="font-playfair text-xl font-semibold text-brown-dark">
                              WhatsApp
                            </h4>
                          </div>

                          <div
                            onDragOver={(e) => handleQrDragOver(e, "whatsapp")}
                            onDragLeave={() => handleQrDragLeave("whatsapp")}
                            onDrop={(e) => handleQrDrop(e, "whatsapp")}
                            className={`relative border-2 border-dashed rounded-lg p-6 transition-all duration-300 ${
                              isDraggingWhatsapp
                                ? "border-primary bg-primary/10 scale-105"
                                : "border-brown-light/30 bg-white hover:border-primary/50 hover:bg-primary/5"
                            }`}
                          >
                            {qrWhatsapp ? (
                              <div className="relative group">
                                <img
                                  src={qrWhatsapp}
                                  alt="QR WhatsApp"
                                  className="w-full h-48 object-contain rounded-lg"
                                />
                                <button
                                  onClick={() => handleQrRemove("whatsapp")}
                                  className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                  aria-label="Eliminar QR de WhatsApp"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <label className="flex flex-col items-center justify-center cursor-pointer min-h-[200px]">
                                <Upload className="h-12 w-12 text-brown-light/50 mb-3" />
                                <p className="text-brown-dark font-medium mb-1">
                                  Arrastra tu QR aquí
                                </p>
                                <p className="text-sm text-brown-light/70 mb-3">
                                  o haz clic para seleccionar
                                </p>
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) =>
                                    handleQrFileSelect(e, "whatsapp")
                                  }
                                  className="hidden"
                                />
                              </label>
                            )}
                          </div>
                        </div>
                      </div>

                      <Alert className="mt-6 bg-blue-50 border-blue-200">
                        <Info className="h-4 w-4 text-blue-600" />
                        <AlertDescription className="text-brown-light/80">
                          Los códigos QR son opcionales. Puedes omitirlos si
                          prefieres una portada interna limpia.
                        </AlertDescription>
                      </Alert>
                    </CardContent>
                  </Card>
                </div>

                {/* Sección de Personalización Visual */}
                <div className="mt-20 max-w-6xl mx-auto">
                  <div className="text-center mb-6">
                    <h3 className="font-playfair text-3xl md:text-4xl font-semibold text-brown mb-2">
                      Personaliza tu Portada
                    </h3>
                    <p className="text-brown-light/90 text-base">
                      Elige entre subir tu logo con colores personalizados o
                      selecciona un estampado de nuestra colección.
                    </p>
                  </div>

                  {/* Grid de Preview y Controles - Estilo old-live-preview */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Preview - Izquierda */}
                    <div className="order-2 lg:order-1">
                      <div className="space-y-4">
                        {/* Preview con sistema de capas */}
                        <div ref={previewRef}>
                          <PreviewCapas
                            portadaConfig={portadaConfig}
                            cubiertaUrl={
                              tieneArgollas
                                ? "https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/general/agenda-base-con-argollas.png"
                                : "https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/general/agenda-base-sin-argollas.png"
                            }
                            nombrePersonalizado={personalizedName}
                            orientacion="vertical"
                            estampadosDisponibles={ESTAMPADOS as Estampado[]}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Controles - Derecha */}
                    <div className="order-1 lg:order-2 space-y-6">
                      <div className="bg-white p-6 rounded-xl border-2 border-gray-200 space-y-6">
                        <h2 className="text-xl font-bold text-brown-dark">
                          Opciones de Diseño
                        </h2>

                        {/* Selector de tipo con botones */}
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-brown-dark">
                            Tipo de Portada
                          </label>
                          <div className="grid grid-cols-3 gap-2">
                            <button
                              onClick={() =>
                                handlePersonalizationTypeChange("logo")
                              }
                              className={`px-3 py-2 border-2 rounded-lg transition-all text-xs font-medium flex items-center justify-center gap-1 ${
                                personalizationType === "logo"
                                  ? "border-brown-dark bg-brown-dark text-white"
                                  : "border-gray-300 hover:border-gray-400 text-brown-dark"
                              }`}
                            >
                              <Palette className="h-4 w-4" />
                              <span className="hidden sm:inline">Logo</span>
                            </button>
                            <button
                              onClick={() =>
                                handlePersonalizationTypeChange("estampado")
                              }
                              className={`px-3 py-2 border-2 rounded-lg transition-all text-xs font-medium flex items-center justify-center gap-1 ${
                                personalizationType === "estampado"
                                  ? "border-brown-dark bg-brown-dark text-white"
                                  : "border-gray-300 hover:border-gray-400 text-brown-dark"
                              }`}
                            >
                              <ImageIcon className="h-4 w-4" />
                              <span className="hidden sm:inline">
                                Estampado
                              </span>
                            </button>
                            <button
                              onClick={() =>
                                handlePersonalizationTypeChange(
                                  "estampado-personalizado"
                                )
                              }
                              className={`px-3 py-2 border-2 rounded-lg transition-all text-xs font-medium flex items-center justify-center gap-1 ${
                                personalizationType ===
                                "estampado-personalizado"
                                  ? "border-brown-dark bg-brown-dark text-white"
                                  : "border-gray-300 hover:border-gray-400 text-brown-dark"
                              }`}
                            >
                              <Upload className="h-4 w-4" />
                              <span className="hidden sm:inline">Tuyo</span>
                            </button>
                          </div>
                        </div>

                        {/* Selector de argollas (visible para todos los tipos) */}
                        {personalizationType !== null && (
                          <div className="space-y-2">
                            <label className="text-sm font-semibold text-brown-dark">
                              Estilo de Lomo
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                              <button
                                onClick={() => setTieneArgollas(true)}
                                className={`px-3 py-2.5 border-2 rounded-lg transition-all text-sm font-medium ${
                                  tieneArgollas
                                    ? "border-brown-dark bg-brown-dark text-white"
                                    : "border-gray-300 hover:border-gray-400 text-brown-dark"
                                }`}
                              >
                                Con Argollas
                              </button>
                              <button
                                onClick={() => setTieneArgollas(false)}
                                className={`px-3 py-2.5 border-2 rounded-lg transition-all text-sm font-medium ${
                                  !tieneArgollas
                                    ? "border-brown-dark bg-brown-dark text-white"
                                    : "border-gray-300 hover:border-gray-400 text-brown-dark"
                                }`}
                              >
                                Sin Argollas
                              </button>
                            </div>
                            <p className="text-xs text-gray-500">
                              Elige el estilo de la cubierta de tu agenda
                            </p>
                          </div>
                        )}

                        {/* Nombre personalizado (opcional) */}
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-brown-dark">
                            Tu Nombre (opcional)
                          </label>
                          <input
                            type="text"
                            maxLength={40}
                            value={personalizedName}
                            onChange={(e) =>
                              setPersonalizedName(e.target.value)
                            }
                            placeholder="Ej: María Fernández"
                            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-brown-dark focus:outline-none transition-all"
                          />
                          <p className="text-xs text-gray-500">
                            Aparecerá en la parte inferior de la portada y en el
                            lomo de la agenda
                          </p>
                        </div>

                        {/* Contenido condicional: Logo */}
                        {personalizationType === "logo" && (
                          <>
                            {/* Upload Logo */}
                            <div className="space-y-2">
                              <label className="text-sm font-semibold text-brown-dark">
                                Sube tu Logo
                              </label>
                              <div
                                onDragOver={handleLogoDragOver}
                                onDragLeave={handleLogoDragLeave}
                                onDrop={handleLogoDrop}
                                className={`border-2 border-dashed rounded-lg p-4 text-center transition-all ${
                                  isDraggingLogo
                                    ? "border-primary bg-primary/10"
                                    : "border-gray-300"
                                }`}
                              >
                                {logo ? (
                                  <div className="relative group">
                                    <img
                                      src={logo}
                                      alt="Logo"
                                      className="w-full max-h-32 object-contain"
                                    />
                                    <button
                                      onClick={handleLogoRemove}
                                      className="absolute top-0 right-0 bg-red-500 hover:bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </div>
                                ) : (
                                  <label className="cursor-pointer flex flex-col items-center gap-2 py-4">
                                    <Upload className="h-8 w-8 text-gray-400" />
                                    <span className="text-sm text-gray-600">
                                      Haz clic para subir
                                    </span>
                                    <span className="text-xs text-gray-400">
                                      PNG, JPG hasta 5MB
                                    </span>
                                    <input
                                      type="file"
                                      accept="image/*"
                                      onChange={handleLogoFileSelect}
                                      className="hidden"
                                    />
                                  </label>
                                )}
                              </div>
                            </div>

                            {/* Paleta de colores */}
                            {logo && (
                              <div className="space-y-4">
                                <label className="text-sm font-semibold text-brown-dark">
                                  Color de Fondo del Recuadro
                                </label>

                                {/* Colores detectados del logo */}
                                {logoPalette.length > 0 && (
                                  <div className="space-y-2">
                                    <p className="text-xs text-gray-500">
                                      Colores detectados de tu logo
                                    </p>
                                    <div className="grid grid-cols-4 gap-2">
                                      {logoPalette
                                        .slice(0, 8)
                                        .map((color, index) => (
                                          <button
                                            key={`detected-${index}`}
                                            onClick={() =>
                                              setSelectedColor(color)
                                            }
                                            className={`aspect-square rounded-lg border-2 transition-all hover:scale-110 ${
                                              selectedColor === color
                                                ? "border-brown-dark ring-2 ring-offset-2 ring-brown-dark"
                                                : "border-gray-300"
                                            }`}
                                            style={{ backgroundColor: color }}
                                            title={color}
                                            aria-label={`Color ${color}`}
                                          />
                                        ))}
                                    </div>
                                  </div>
                                )}

                                {/* Colores predeterminados */}
                                <div className="space-y-2">
                                  <p className="text-xs text-gray-500">
                                    Colores predeterminados
                                  </p>
                                  <div className="grid grid-cols-6 gap-2">
                                    {COLORES_PREDETERMINADOS.map(
                                      (color, index) => (
                                        <button
                                          key={`preset-${index}`}
                                          onClick={() =>
                                            setSelectedColor(color.hex)
                                          }
                                          className={`aspect-square rounded-lg border-2 transition-all hover:scale-110 ${
                                            selectedColor === color.hex
                                              ? "border-brown-dark ring-2 ring-offset-2 ring-brown-dark"
                                              : "border-gray-300"
                                          }`}
                                          style={{ backgroundColor: color.hex }}
                                          title={color.name}
                                          aria-label={color.name}
                                        />
                                      )
                                    )}
                                  </div>
                                </div>

                                {selectedColor && (
                                  <p className="text-xs text-gray-500 text-center mt-2">
                                    <span className="font-mono">
                                      {selectedColor}
                                    </span>
                                  </p>
                                )}
                              </div>
                            )}
                          </>
                        )}

                        {/* Contenido condicional: Estampado */}
                        {personalizationType === "estampado" && (
                          <div className="space-y-2">
                            <label className="text-sm font-semibold text-brown-dark">
                              Selecciona un Estampado
                            </label>
                            <div className="grid grid-cols-3 gap-2 max-h-80 overflow-y-auto p-1">
                              {ESTAMPADOS.map((estampado) => (
                                <button
                                  key={estampado.id}
                                  onClick={() =>
                                    handleEstampadoSelect(estampado.id)
                                  }
                                  className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all hover:scale-105 ${
                                    selectedEstampado === estampado.id
                                      ? "border-brown-dark ring-2 ring-offset-2 ring-brown-dark"
                                      : "border-gray-300"
                                  }`}
                                >
                                  <img
                                    src={estampado.path}
                                    alt={estampado.name}
                                    className="w-full h-full object-cover"
                                  />
                                  {selectedEstampado === estampado.id && (
                                    <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                                      <Check className="h-6 w-6 text-brown-dark drop-shadow-lg" />
                                    </div>
                                  )}
                                </button>
                              ))}
                            </div>
                            {selectedEstampado && (
                              <p className="text-xs text-gray-600 text-center mt-2">
                                {
                                  ESTAMPADOS.find(
                                    (e) => e.id === selectedEstampado
                                  )?.name
                                }
                              </p>
                            )}
                          </div>
                        )}

                        {/* Contenido condicional: Estampado Personalizado */}
                        {personalizationType === "estampado-personalizado" && (
                          <div className="space-y-2">
                            <label className="text-sm font-semibold text-brown-dark">
                              Sube tu Estampado
                            </label>
                            <div
                              onDragOver={handleCustomEstampadoDragOver}
                              onDragLeave={handleCustomEstampadoDragLeave}
                              onDrop={handleCustomEstampadoDrop}
                              className={`border-2 border-dashed rounded-lg p-4 text-center transition-all ${
                                isDraggingCustomEstampado
                                  ? "border-primary bg-primary/10"
                                  : "border-gray-300"
                              }`}
                            >
                              {customEstampado ? (
                                <div className="relative group">
                                  <img
                                    src={customEstampado}
                                    alt="Estampado personalizado"
                                    className="w-full max-h-64 object-contain rounded-lg"
                                  />
                                  <button
                                    onClick={handleCustomEstampadoRemove}
                                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              ) : (
                                <label className="cursor-pointer flex flex-col items-center gap-3 py-8">
                                  <div className="bg-brown-light/10 p-4 rounded-full">
                                    <Upload className="h-10 w-10 text-brown-dark" />
                                  </div>
                                  <div className="space-y-1">
                                    <p className="text-sm font-medium text-brown-dark">
                                      Arrastra tu imagen aquí
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      o haz clic para seleccionar
                                    </p>
                                  </div>
                                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                                    <p className="text-xs text-blue-800 flex items-start gap-2">
                                      <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                                      <span>
                                        Tu imagen cubrirá toda la portada de la
                                        agenda. Recomendamos imágenes de alta
                                        calidad (mín. 1000x1400px). Máx. 5MB en
                                        formato JPG o PNG.
                                      </span>
                                    </p>
                                  </div>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleCustomEstampadoFileSelect}
                                    className="hidden"
                                  />
                                </label>
                              )}
                            </div>
                            {customEstampado && (
                              <p className="text-xs text-green-600 text-center mt-2 flex items-center justify-center gap-1">
                                <Check className="h-3 w-3" />
                                Estampado personalizado cargado
                              </p>
                            )}
                          </div>
                        )}

                        {/* Info Alert */}
                        {personalizationType && (
                          <Alert className="bg-blue-50 border-blue-200">
                            <Info className="h-4 w-4 text-blue-600" />
                            <AlertDescription className="text-xs text-gray-700">
                              {personalizationType === "logo"
                                ? "Tu logo se imprimirá en un recuadro central de la portada, con el color seleccionado como fondo de ese recuadro."
                                : personalizationType === "estampado"
                                ? "El estampado seleccionado cubrirá toda la portada de tu agenda."
                                : "Tu imagen personalizada cubrirá toda la portada de la agenda, creando un diseño único y exclusivo."}
                            </AlertDescription>
                          </Alert>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* FIN COLUMNA IZQUIERDA */}

              {/* COLUMNA DERECHA (1/3) - Resumen Sticky */}
              <div className="lg:col-span-1">
                <PersonalizaAgendaResumen
                  selectedSections={selectedSectionsData}
                  variantesSeleccionadas={variantesSeleccionadas}
                  tableroBorrableType={tableroBorrableType}
                  qrInstagram={qrInstagram}
                  qrWhatsapp={qrWhatsapp}
                  personalizationType={personalizationType}
                  logo={logo}
                  selectedColor={selectedColor}
                  selectedEstampado={selectedEstampado}
                  customEstampado={customEstampado}
                  totalPagesUsed={totalPagesUsed}
                  pagesRemaining={pagesRemaining}
                  onAddToCart={handleAddToCart}
                />
              </div>
              {/* FIN COLUMNA DERECHA */}
            </div>
            {/* FIN Layout de 2 columnas */}
          </div>
        </div>
      </section>

      {/* CTA Final - Ver Colección Completa */}
      <section className="py-24 bg-[#F8F6F4]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-sm uppercase tracking-wide text-primary font-medium mb-6">
              Si necesitas ayuda, al deslizar encontrarás un tutorial
            </p>
            <Button
              variant="outline"
              onClick={() => navigate("/products")}
              className="border-2 border-primary hover:bg-primary hover:text-brown-dark text-brown-dark font-semibold px-10 py-4 rounded-full text-lg shadow-md transition-all duration-300"
            >
              Ver la colección completa
            </Button>
          </div>
        </div>
      </section>

      {/* Modal genérico de variantes de sección */}
      <Dialog
        open={variantesModalSeccion !== null}
        onOpenChange={(open) => !open && setVariantesModalSeccion(null)}
      >
        <DialogContent className="max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-playfair text-2xl text-brown-dark text-center">
              Elige el formato de{" "}
              {variantesModalSeccion?.nombre}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4">
            {variantesModalSeccion?.variantes.map((variante) => (
              <Card
                key={variante.slug}
                onClick={() => handleVarianteSelect(variante.slug)}
                className="cursor-pointer hover:shadow-xl hover:scale-105 transition-all duration-300 bg-[#F8F6F4]"
              >
                <CardContent className="p-6 flex flex-col items-center gap-4">
                  <div className="w-full aspect-[3/4] max-h-40 bg-white border-2 border-brown-light/30 rounded-lg flex items-center justify-center overflow-hidden">
                    {variante.imageUrl ? (
                      <img
                        src={variante.imageUrl}
                        alt={variante.nombre}
                        className="w-full h-full object-contain p-2"
                      />
                    ) : (
                      <span className="text-4xl">📄</span>
                    )}
                  </div>
                  <h3 className="font-playfair font-semibold text-lg text-brown-dark text-center">
                    {variante.nombre}
                  </h3>
                  {variante.descripcion && (
                    <p className="text-sm text-brown-light/80 text-center">
                      {variante.descripcion}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal para seleccionar tipo de Tablero Borrable */}
      <Dialog
        open={tableroBorrableModalOpen}
        onOpenChange={setTableroBorrableModalOpen}
      >
        <DialogContent className="max-w-2xl w-full">
          <DialogHeader>
            <DialogTitle className="font-playfair text-2xl text-brown-dark text-center">
              Elige el tipo de Tablero Borrable
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4">
            {/* Semana */}
            <Card
              onClick={() => handleTableroBorrableTypeSelect("semana")}
              className="cursor-pointer hover:shadow-xl hover:scale-105 transition-all duration-300 bg-[#F8F6F4]"
            >
              <CardContent className="p-6 flex flex-col items-center gap-4">
                <div className="w-full aspect-[4/3] max-h-32 bg-white border-2 border-brown-light/30 rounded-lg flex items-center justify-center overflow-hidden">
                  <img
                    src="https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/general/TABLERO%20BORRABLE%20SEMANA.png"
                    alt="Tablero Borrable Semana"
                    className="w-full h-full object-contain p-2 scale-90"
                  />
                </div>
                <h3 className="font-playfair font-semibold text-lg text-brown-dark text-center">
                  Semana
                </h3>
                <p className="text-sm text-brown-light/80 text-center">
                  Organiza tus tareas semanales
                </p>
              </CardContent>
            </Card>

            {/* Ideas / Bocetos */}
            <Card
              onClick={() => handleTableroBorrableTypeSelect("ideas")}
              className="cursor-pointer hover:shadow-xl hover:scale-105 transition-all duration-300 bg-[#F8F6F4]"
            >
              <CardContent className="p-6 flex flex-col items-center gap-4">
                <div className="w-full aspect-[3/4] max-h-32 bg-white border-2 border-brown-light/30 rounded-lg flex items-center justify-center overflow-hidden">
                  <img
                    src="https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/general/TABLERO%20BORRABLE%20IDEAS.png"
                    alt="Tablero Borrable Ideas"
                    className="w-full h-full object-contain p-2"
                  />
                </div>
                <h3 className="font-playfair font-semibold text-lg text-brown-dark text-center">
                  Ideas / Bocetos
                </h3>
                <p className="text-sm text-brown-light/80 text-center">
                  Espacio libre para ideas y bocetos
                </p>
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal para vista ampliada de imagen */}
      <Dialog open={imageModalOpen} onOpenChange={setImageModalOpen}>
        <DialogContent className="max-w-4xl w-full">
          <DialogHeader>
            <DialogTitle className="font-playfair text-2xl text-brown-dark">
              {selectedImage?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center bg-white rounded-lg p-4">
            {selectedImage && (
              <img
                src={selectedImage.url}
                alt={selectedImage.name}
                className="w-full h-auto max-h-[70vh] object-contain"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Botón flotante para scroll a personalización */}
      {showScrollHint && (
        <div className="fixed bottom-6 right-6 z-50 animate-fade-in">
          <button
            onClick={scrollToPersonalizacion}
            className="group flex items-center gap-2.5 bg-primary/95 hover:bg-primary text-brown-dark px-5 py-3.5 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 backdrop-blur-sm border border-primary-dark/30 hover:scale-105"
            aria-label="Comenzar a personalizar"
          >
            <span className="text-sm font-semibold whitespace-nowrap">
              Comenzar a personalizar
            </span>
            <ArrowDown className="h-5 w-5 group-hover:translate-y-0.5 transition-transform" />
          </button>
        </div>
      )}
    </div>
  );
};

export default PersonalizaAgenda;
